import { TwoBtnsOrderedContainer } from 'components/atoms/Layout'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import StyledAccordion from 'components/molecules/StyledAccordion'
import GoBackHeader from 'components/organisms/GoBackHeader'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import { FC, useEffect, useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import { Link, Redirect, useHistory } from 'react-router-dom'
import {
  getInactiveActiveQuestionnaireSection,
  setActiveSection
} from 'reducers/activesection/activesectionSlice'
import {
  getIconByStatus,
  getQuestionnaireSectionStatusName,
  getSectionButtonText,
  MatterClientPortalQuestionnaireSectionStatus,
  Section
} from 'share/domain/case'
import {
  START_RESUME_QUESTIONNAIRE_SECTION,
  StartResumeQuestionnaireSectionResponse,
  StartResumeQuestionnaireSectionVariables,
  SUBMIT_QUESTIONNAIRE,
  SubmitQuestionnaireResponse,
  SubmitQuestionnaireVariables
} from 'share/mutations/cases'
import {
  GET_CASE_QUESTIONNAIRE_SECTIONS,
  GetCaseQuestionnaireSectionsResponse
} from 'share/queries/cases'

import { useLazyQuery, useMutation } from '@apollo/client'

import { QUESTIONNAIRE_JOINT_CASE_NOTIFICATION } from '../constants'
import QuestionnaireInstructionsModal from '../QuestionnaireInstructionsModal'
import QuestionnaireSubmitToAttorneyModal from '../QuestionnaireSubmitToAttornyModal'
import {
  CardsWrapper,
  QuestionnaireCompleteSectionHelperText,
  QuestionnaireCompletionDateContainer,
  QuestionnaireNotification,
  QuestionnaireOverviewPage,
  QuestionnaireOverviewPageWrapper,
  QuestionnaireSectionSummary
} from './styles'

const getNotificationBadgeClass = (
  isJointCase: boolean | null | undefined,
  defaultClass?: string
) => {
  let notificationClass = defaultClass ? defaultClass : ''
  notificationClass = isJointCase
    ? notificationClass + ' joint'
    : notificationClass + ' single'
  return notificationClass
}

const QuestionnaireOverview: FC = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const activeCase = useAppSelector((state) => state.activeCase)
  const [sections, setSections] = useState<Section[]>([])
  const [sectionId, setSectionId] = useState(0)
  const [toQuestionnaireSectionSummary, setToQuestionnaireSectionSummary] =
    useState(false)
  const [
    showQuestionnaireInstructionsModal,
    setShowQuestionnaireInstructionsModal
  ] = useState(false)

  const [
    showQuestionnaireSubmitToAttornyModal,
    setShowQuestionnaireSubmitToAttornyModal
  ] = useState(false)

  const [showSubmitError, setShowSubmitError] = useState(false)
  const [isSubmitInProgress, setIsSubmitInPrgoress] = useState(false)

  const [sectionsCompleted, setSectionsCompleted] = useState<boolean>(false)

  const submittedImportedSectionsCountRef = useRef(0)

  /**
   * no of sections completed
   */
  const [sectionsCompletedCount, setSectionsCompletedCount] =
    useState<number>(0)

  const [questionnaireRejected, setQuestionnaireRejected] =
    useState<boolean>(false)

  const [questionnaireSubmiited, setQuestionnaireSubmiited] =
    useState<boolean>(false)

  const [rejectedSections, setRejectedSections] = useState<string>('')

  const [startResumeQuestionnaireSection, { loading: startResumeLoading }] =
    useMutation<
      StartResumeQuestionnaireSectionResponse,
      StartResumeQuestionnaireSectionVariables
    >(START_RESUME_QUESTIONNAIRE_SECTION)

  const [submitQuestionnaire] =
    useMutation<SubmitQuestionnaireResponse, SubmitQuestionnaireVariables>(
      SUBMIT_QUESTIONNAIRE
    )

  const isPersonalInfoSectionCompleteRef = useRef(false)

  const [overviewText, setOverviewText] = useState<string>(
    'Please complete the personal information section.'
  )

  function getCaseId() {
    return activeCase?.matter?.id ? activeCase?.matter.id : 0
  }

  const [getQuestionnaireSections, { loading, data, error }] =
    useLazyQuery<GetCaseQuestionnaireSectionsResponse>(
      GET_CASE_QUESTIONNAIRE_SECTIONS,
      {
        fetchPolicy: 'network-only'
      }
    )

  useEffect(() => {
    if (activeCase) {
      if (activeCase.matter) {
        if (activeCase.matter.id) {
          getQuestionnaireSections({
            variables: { caseId: activeCase.matter.id }
          })
        }
      }
    }
    // clear out any questionnaire active sections in redux
    const activeSection = getInactiveActiveQuestionnaireSection()
    dispatch(setActiveSection(activeSection))
  }, [activeCase])

  useEffect(() => {
    if (data && data?.cases.caseQuestionnaireSections.length > 0) {
      setSections(data?.cases.caseQuestionnaireSections)
    }
  }, [data])

  function checkIfPersonalInfoSectionComplete() {
    const personalInfoSection = sections.find((section: Section) => {
      return section.sectionName === 'Personal Information'
    })

    const isCompleted: boolean =
      personalInfoSection &&
      personalInfoSection?.sectionStatus >
        MatterClientPortalQuestionnaireSectionStatus.InProgress
        ? true
        : false

    isPersonalInfoSectionCompleteRef.current = isCompleted
  }

  function checkSectionsCompleted() {
    if (!sections || sections?.length === 0) {
      setSectionsCompleted(false)
      return
    }
    checkIfPersonalInfoSectionComplete()

    const submittedCompletedSections: Section[] = sections.filter((section) => {
      return (
        section.sectionStatus >
          MatterClientPortalQuestionnaireSectionStatus.InProgress &&
        section.sectionStatus !==
          MatterClientPortalQuestionnaireSectionStatus.Rejected
      )
    })

    const submittedImportedSections: Section[] = sections.filter((section) => {
      return (
        section.sectionStatus >
          MatterClientPortalQuestionnaireSectionStatus.Completed &&
        section.sectionStatus !==
          MatterClientPortalQuestionnaireSectionStatus.Rejected
      )
    })

    submittedImportedSectionsCountRef.current = submittedImportedSections.length

    const submittedSections: Section[] = submittedCompletedSections.filter(
      (section) => {
        return (
          section.sectionStatus ===
          MatterClientPortalQuestionnaireSectionStatus.Submitted
        )
      }
    )

    setSectionsCompletedCount(submittedCompletedSections.length)

    if (submittedSections.length === sections.length) {
      setSectionsCompleted(false)
      return
    }
    setSectionsCompleted(
      submittedCompletedSections?.length === sections?.length
    )
  }

  function checkQuestionnaireSubmitted() {
    const sectionsSubmitted = sections.filter((section) => {
      return (
        section.sectionStatus ===
          MatterClientPortalQuestionnaireSectionStatus.Submitted ||
        section.sectionStatus ===
          MatterClientPortalQuestionnaireSectionStatus.Imported
      )
    })
    const sectionsRejected = sections.filter((section) => {
      return section.isRejected === true
    })
    if (sectionsRejected?.length) {
      setQuestionnaireRejected(true)
      setQuestionnaireSubmiited(false)
      const rejectedSections = sectionsRejected
        .map(({ sectionName }) => sectionName)
        .join(', ')
      setRejectedSections(rejectedSections)
      return
    }

    if (sectionsSubmitted?.length) {
      setQuestionnaireRejected(false)
      setQuestionnaireSubmiited(true)
      return
    }

    setQuestionnaireRejected(false)
    setQuestionnaireSubmiited(false)
  }

  useEffect(() => {
    checkSectionsCompleted()
    checkQuestionnaireSubmitted()
  }, [sections])

  async function startResumeQuestionnaireHelper(
    caseId: number,
    sectionId: number
  ): Promise<boolean> {
    let result = false
    try {
      const input = {
        caseId: caseId,
        sectionId: sectionId
      }

      const startResumeResult = await startResumeQuestionnaireSection({
        variables: { request: input }
      })
      if (
        startResumeResult.data?.cases.startResumeQuestionnaireSection.success
      ) {
        result = true
      }
    } catch (e) {
      result = false
    }

    return result
  }

  async function submitQuestionnaireHelper(caseId: number): Promise<boolean> {
    let result = false
    try {
      const input = {
        caseId: caseId
      }

      const submitResult = await submitQuestionnaire({
        variables: { request: input }
      })
      if (submitResult.data?.cases.submitQuestionnaire.success) {
        result = true
      }
    } catch (e) {
      result = false
    }

    return result
  }

  function viewInstructionsClicked() {
    setShowQuestionnaireInstructionsModal(true)
  }

  function closeViewInstructionsModal() {
    setShowQuestionnaireInstructionsModal(false)
  }

  async function startResumeClick(caseId: number, sectionId: number) {
    const section = sections.find((section) => section.id === sectionId)

    if (
      section?.sectionStatus ===
      MatterClientPortalQuestionnaireSectionStatus.Submitted
    ) {
      setSectionId(sectionId)
      setToQuestionnaireSectionSummary(true)
      return
    }
    const startResumeResult = await startResumeQuestionnaireHelper(
      caseId,
      sectionId
    )
    if (startResumeResult) {
      setSectionId(sectionId)
      setToQuestionnaireSectionSummary(true)
    } else {
      return error
      //console.log('Call to Start Resume had an error')
    }
  }

  async function submitQuestionnaireClick() {
    setIsSubmitInPrgoress(true)
    const caseId = getCaseId()
    const submitResult = await submitQuestionnaireHelper(caseId)
    if (submitResult) {
      getQuestionnaireSections({
        variables: { caseId: caseId }
      })
      setIsSubmitInPrgoress(false)
      setShowSubmitError(false)
      setShowQuestionnaireSubmitToAttornyModal(false)
      console.log('submitted the questionnaire successfully')
    } else {
      setIsSubmitInPrgoress(false)
      setShowSubmitError(true)
      console.log('error submitting the questionnaire')
    }
  }

  function handleSubmitToAttorny() {
    setShowQuestionnaireSubmitToAttornyModal(true)
  }

  function cancelSubmitToAttorney() {
    setShowSubmitError(false)
    setShowQuestionnaireSubmitToAttornyModal(false)
  }

  useEffect(() => {
    if (questionnaireRejected) {
      setOverviewText(
        'You may only submit to your attorney once you have completed all sections of the questionnaire.'
      )

      return
    }
    if (questionnaireSubmiited && !questionnaireRejected) {
      setOverviewText(
        'Your questionnaire is available to view in a read-only state. Please wait for your attorney to review your questionnaire.'
      )

      return
    }
    if (isPersonalInfoSectionCompleteRef.current) {
      setOverviewText(
        'You may only submit to your attorney once you have completed all sections of the questionnaire.'
      )
    }
  }, [
    questionnaireRejected,
    questionnaireSubmiited,
    isPersonalInfoSectionCompleteRef.current
  ])

  function goBackHeaderOnClick() {
    history.push('/dashboard/case')
  }

  function shouldShowCardButton(sectionStatus: number) {
    return (
      sectionStatus !== MatterClientPortalQuestionnaireSectionStatus.Imported
    )
  }

  if (error) {
    return <div>{error?.message}</div>
  }

  if (toQuestionnaireSectionSummary)
    return (
      <Redirect to={`/dashboard/questionnaires/sectionsummary/${sectionId}`} />
    )

  return (
    <>
      <GoBackHeader
        text='Bankruptcy Questionnaire'
        onClick={goBackHeaderOnClick}
      />
      <QuestionnaireOverviewPageWrapper data-testid='questionnaire-overview-page'>
        {(loading || startResumeLoading) && loadingSpinnerPortal()}
        {questionnaireRejected && (
          <QuestionnaireNotification
            className={getNotificationBadgeClass(
              activeCase.matter?.isJoint,
              'questionnaire-rejected'
            )}
          >
            <div style={{ display: 'flex' }}>
              <span className='mdi mdi-alert mr-1'></span>
              <span data-testid='questionnaire-rejected-notification-text'>
                Your attorney has sent back your {rejectedSections} section with
                a comment. Please review and edit your information before
                re-submitting your questionnaire.
              </span>
            </div>
          </QuestionnaireNotification>
        )}
        {questionnaireSubmiited && (
          <QuestionnaireNotification
            className={getNotificationBadgeClass(
              activeCase.matter?.isJoint,
              'questionnaire-submitted'
            )}
          >
            <div style={{ display: 'flex' }}>
              <span className='mdi mdi-check-circle mr-1'></span>
              <span data-testid='questionnaire-submitted-notification-text'>
                Your questionnaire has been successfully submitted to your
                attorney. Please{' '}
                <Link to='/dashboard/lawfirminformation'>
                  contact your attorney
                </Link>{' '}
                if you have any questions.
              </span>
            </div>
          </QuestionnaireNotification>
        )}
        {activeCase.matter?.isJoint && (
          <QuestionnaireNotification className='joint'>
            <div style={{ display: 'flex' }}>
              <span className='mdi mdi-information blue-info-icon mr-1'></span>
              <span data-testid='questionnaire-overview-joint-notification'>
                {QUESTIONNAIRE_JOINT_CASE_NOTIFICATION}
              </span>
            </div>
          </QuestionnaireNotification>
        )}
        <QuestionnaireOverviewPage data-testid='questionnaire-overview-page-wrapper'>
          <h2>Questionnaire Overview</h2>
          {!!activeCase?.matter?.questionnaireExpectedCompletionDate && (
            <QuestionnaireCompletionDateContainer>
              <span className='mdi mdi-calendar-outline mr-1'></span>
              <span data-testid='questionnaire-completion-deadline'>
                Deadline to complete questionnaire:{' '}
                {activeCase?.matter?.questionnaireExpectedCompletionFormatted
                  ? activeCase?.matter?.questionnaireExpectedCompletionFormatted
                  : ''}
              </span>
            </QuestionnaireCompletionDateContainer>
          )}
          <QuestionnaireCompleteSectionHelperText>
            {overviewText}
          </QuestionnaireCompleteSectionHelperText>
          <TwoBtnsOrderedContainer className='mb-3'>
            <Button
              variant='outline-secondary'
              onClick={viewInstructionsClicked}
              data-testid='questionnaire-overview-view-instructions-button'
            >
              <span className='mdi mdi-information-outline mr-2' />
              Questionnaire Instructions
            </Button>
            <Button
              variant='outline-secondary'
              onClick={() => {
                handleSubmitToAttorny()
              }}
              disabled={!sectionsCompleted}
              data-testid='questionnaire-overview-submit-to-attorney-button'
            >
              Submit to attorney
            </Button>
          </TwoBtnsOrderedContainer>
          <hr className='mb-4' />
          <QuestionnaireSectionSummary>
            <h4>Questionnaire Sections</h4>
            <h6>
              Sections completed {sectionsCompletedCount} out of{' '}
              {sections.length}
            </h6>
          </QuestionnaireSectionSummary>
          <CardsWrapper data-testid='questionnaire-overview-cards-wrapper'>
            {sections.map((section, index) => (
              <Card key={section.id}>
                <div className='row text-left'>
                  <div
                    className='col-12'
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <h3 data-testid={`questionnaire-section-name-${index}`}>
                      {section.sectionName}
                    </h3>
                    <span
                      className={`${getIconByStatus(section.sectionStatus)}`}
                    />
                  </div>
                </div>

                <div className='row text-left'>
                  <div className='col-12'>
                    <span className='font-bold'>Status: </span>
                    <span
                      className='font-bold'
                      data-testid={`questionnaire-section-status-${index}`}
                    >
                      {getQuestionnaireSectionStatusName(section.sectionStatus)}
                    </span>
                  </div>
                </div>

                <StyledAccordion>
                  <div className='row'>
                    <div className='col-12  text-left mb-4'>
                      <span className='font-bold mr-1'>Description:</span>
                      <span
                        className='font-light'
                        data-testid={`questionnaire-section-description-${index}`}
                      >
                        {section?.sectionCardSummary?.split('\\n').join('\n')}
                      </span>
                    </div>
                  </div>
                </StyledAccordion>

                {shouldShowCardButton(section.sectionStatus) && (
                  <Button
                    className='ui-go-to-case-btn'
                    onClick={() => {
                      startResumeClick(getCaseId(), section.id).catch(
                        (error) => {
                          console.log(
                            'Call to Start Resume had an error',
                            error
                          )
                        }
                      )
                    }}
                    data-testid={`questionnaire-start-resume-button-${index}`}
                    variant={
                      section.sectionStatus ===
                        MatterClientPortalQuestionnaireSectionStatus.Completed ||
                      section.sectionStatus ===
                        MatterClientPortalQuestionnaireSectionStatus.Submitted ||
                      section.sectionStatus ===
                        MatterClientPortalQuestionnaireSectionStatus.Imported
                        ? 'outline-secondary'
                        : 'primary'
                    }
                    disabled={!section.isDisplayEnabled}
                  >
                    {getSectionButtonText(section.sectionStatus)}
                  </Button>
                )}
              </Card>
            ))}
          </CardsWrapper>
        </QuestionnaireOverviewPage>

        <QuestionnaireInstructionsModal
          show={showQuestionnaireInstructionsModal}
          caseId={getCaseId()}
          onHide={closeViewInstructionsModal}
        />
        <QuestionnaireSubmitToAttorneyModal
          show={showQuestionnaireSubmitToAttornyModal}
          caseId={getCaseId()}
          onHide={cancelSubmitToAttorney}
          onSubmit={submitQuestionnaireClick}
          showError={showSubmitError}
          isLoading={isSubmitInProgress}
        />
      </QuestionnaireOverviewPageWrapper>
    </>
  )
}

export default QuestionnaireOverview
